"use client";

import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import Button from "./Button";
import classNames from "classnames";

interface PopupProps {
  heading: string | React.ReactNode;
  subheading?: string | React.ReactNode;
  primaryButtonLabel: string;
  secondaryButtonLabel?: string;
  onPrimaryClick: () => void;
  onSecondaryClick?: () => void;
  isOpen: boolean;
  onClose?: () => void; // For backdrop/Escape key
  className?: string;
  /** Stable selector for QA automation; sub-elements derive from it. */
  testId?: string;
}

const Popup: React.FC<PopupProps> = ({
  heading,
  subheading,
  primaryButtonLabel,
  secondaryButtonLabel,
  onPrimaryClick,
  onSecondaryClick,
  isOpen,
  onClose,
  className,
  testId = "popup",
}) => {
  // Handle Escape key press
  useEffect(() => {
    if (!isOpen || !onClose) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = () => {
    onClose?.();
  };

  // Prevent modal content clicks from closing the popup
  const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  const popupContent = (
    <div
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="popup-heading"
    >
      {/* Backdrop */}
      <div
        data-testid={`${testId}-backdrop`}
        className="absolute inset-0 bg-black/50"
        onClick={handleBackdropClick}
      />

      {/* Modal Container */}
      <div
        data-testid={`${testId}-content`}
        className={classNames(
          "relative bg-white rounded-lg shadow-lg max-w-sm w-full mx-4",
          className,
        )}
        onClick={handleModalClick}
      >
        <div className="p-6">
          {/* Heading */}
          <h2
            id="popup-heading"
            data-testid={`${testId}-heading`}
            className={"text-lg font-bold text-font-1 mb-6 text-center"}
          >
            {heading}
          </h2>

          {/* Subheading */}
          {subheading && (
            <div
              data-testid={`${testId}-subheading`}
              className="text-base text-font-1 mb-6 leading-relaxed text-center"
            >
              {subheading}
            </div>
          )}

          {/* Button Container */}
          <div
            className={classNames(
              "flex gap-3",
              secondaryButtonLabel ? "" : "flex-col",
            )}
          >
            {/* Secondary Button */}
            {secondaryButtonLabel && (
              <div className="flex-1">
                <Button
                  className="rounded-3xl w-full"
                  label={secondaryButtonLabel}
                  variant="outlined"
                  testId={`${testId}-secondary-button`}
                  onClick={() => {
                    if (onSecondaryClick) {
                      onSecondaryClick();
                    }
                  }}
                />
              </div>
            )}

            {/* Primary Button */}
            <div className={secondaryButtonLabel ? "flex-1" : "w-full"}>
              <Button
                className="rounded-3xl w-full"
                label={primaryButtonLabel}
                variant="outlined"
                testId={`${testId}-primary-button`}
                onClick={onPrimaryClick}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render using portal to document body
  if (typeof window !== "undefined") {
    return createPortal(popupContent, document.body);
  }

  return null;
};

export default Popup;
